/**
 * Автозамок у вебі — за бажанням ([ADR-0045], доповнює [ADR-0019]).
 *
 * Типово вимкнений: ADR-0019 лишається чинним для всіх, хто нічого не
 * вмикав. Увімкнений — після N хвилин у фоні ключ даних забувається так
 * само, як на «Вийти на цьому пристрої», і повертає його лише пароль.
 *
 * Рахується **час у фоні**, а не бездіяльність: картка M5 каже «скільки
 * Береста може лежати у фоні». Момент приховування лягає в `localStorage`,
 * а не в памʼять — інакше закрита й відкрита за годину вкладка
 * розблокувалася б сама, бо обгортка пристрою відмикається без пароля.
 *
 * Перевіряється при поверненні, а не таймером у фоні: браузери
 * пригальмовують таймери схованих вкладок, і замок, що спрацьовує «коли
 * встигне», гірший за замок, що спрацьовує в передбачуваний момент.
 */

export type LockAfter = "off" | "0" | "1" | "5" | "15";
export const LOCK_STEPS: readonly Exclude<LockAfter, "off">[] = ["0", "1", "5", "15"];

const SETTING_KEY = "beresta:autolock";
const HIDDEN_KEY = "beresta:hidden-at";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Приватне вікно: замок діятиме до кінця вкладки, і це чесніше за падіння.
  }
}

export function lockAfter(): LockAfter {
  const saved = read(SETTING_KEY);
  return saved === "0" || saved === "1" || saved === "5" || saved === "15" ? saved : "off";
}

export function setLockAfter(value: LockAfter): void {
  write(SETTING_KEY, value === "off" ? null : value);
  // Старий момент приховування не має замкнути одразу після ввімкнення.
  write(HIDDEN_KEY, null);
}

/** Чи пора замикати. Годинник назад — теж «пора»: невідомо, скільки минуло. */
export function shouldLock(setting: LockAfter, hiddenAt: number | null, now: number): boolean {
  if (setting === "off" || hiddenAt === null) return false;
  const away = now - hiddenAt;
  return away < 0 || away >= Number(setting) * 60_000;
}

function hiddenAt(): number | null {
  const raw = read(HIDDEN_KEY);
  const at = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(at) ? at : null;
}

/**
 * Для старту: чи треба замкнути, ще не відмикаючи. Момент приховування
 * стирається в будь-якому разі — він відповів на своє питання.
 */
export function lockDueAtStart(now = Date.now()): boolean {
  const due = shouldLock(lockAfter(), hiddenAt(), now);
  write(HIDDEN_KEY, null);
  return due;
}

/**
 * Стежити за вкладкою: сховалася — запамʼятати коли; повернулася — спитати
 * `shouldLock` і, якщо пора, покликати `lock`. Повертає відписку.
 */
export function watchAutoLock(lock: () => void): () => void {
  const onChange = () => {
    if (lockAfter() === "off") return;
    if (document.visibilityState === "hidden") {
      write(HIDDEN_KEY, String(Date.now()));
      return;
    }
    if (lockDueAtStart()) lock();
  };
  document.addEventListener("visibilitychange", onChange);
  return () => document.removeEventListener("visibilitychange", onChange);
}
